import Loader from 'Components/Common/Loader'
import { PRIVILEGE } from 'constants/privilege'
import withAccessControl from 'hoc/withAccessControl'
import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fetchWalletThunk } from 'slices/wallet/thunk'
import { RootState, AppDispatch } from 'store'
import AccessControl from 'wrappers/AccessControl'
import "./Wallet.scss"
import GSTInput from 'Components/wallet/GSTInput'
import { createVBAAccount } from 'services/api/wallet'
import InfoPopover from 'Components/Common/InfoPopover'
import CopyButton from 'Components/Common/CopyButton'
import { textToTitleCase } from 'utils/helpers'


const filters = [
    {
        label: 'Payments Blocked',
        value: 'blocked'
    },
    // {
    //     label: 'Paid to Creator',
    //     value: 'paid'
    // },
    {
        label: 'Credit',
        value: 'credit'
    },
    {
        label: 'Debit',
        value: 'debit'
    },
    // {
    //     label: 'Wallet',
    //     value: 'wallet'
    // },


]

const Wallet = () => {

    const { wallet, loading } = useSelector((state: RootState) => state.walletState);
    const dispatch = useDispatch<AppDispatch>();
    const [creatingVBA, setCreatingVBA] = React.useState<boolean>(false);
    const selectedAgencyId = useSelector((state: RootState) => state.auth.selectedAgencyId);

    const [selectedFilter, setSelectedFilter] = React.useState<string>('');
    const [searchQuery, setSearchQuery] = React.useState<string>('');

    useEffect(() => {
        dispatch(fetchWalletThunk({ force: true }));
    }, [])

    const createVBA = async () => {
        if (!selectedAgencyId) return;
        setCreatingVBA(true);
        try {
            const success = await createVBAAccount(selectedAgencyId);
            if (success) {
                dispatch(fetchWalletThunk({ force: true }));
            }
        } finally {
            setCreatingVBA(false);
        }
    }

    const filteredTxns = useMemo(() => {
        if (!selectedFilter) return wallet?.transactions || [];

        if (selectedFilter === 'blocked') {
            return wallet?.blocked || [];
        }

        return wallet?.transactions.filter(txn => {
            switch (selectedFilter) {
                case 'paid':
                    return txn.category === 'influencer';
                case 'credit':
                    return Number(txn.cr_amount ?? '0') > Number(txn.dr_amount ?? '0');
                case 'debit':
                    return Number(txn.dr_amount ?? '0') > Number(txn.cr_amount ?? '0');
                case 'wallet':
                    return txn.category === 'deposit';
                default:
                    return true;
            }
        }) || [];

    }, [wallet, selectedFilter]);

    const txnsMatchingWithSearchQuery = useMemo(() => {
        if (!filteredTxns) return [];
        return filteredTxns.filter(txn => {
            if (searchQuery.trim()) {
                const query = searchQuery.trim().toLowerCase();
                const influencerName = txn.details?.influencer_name?.toLowerCase() || '';
                const campaignName = txn.details?.campaign_name?.toLowerCase() || '';
                if (!influencerName.includes(query) && !campaignName.includes(query)) {
                    return false;
                }
            }
            return true;
        });
    }, [filteredTxns, searchQuery]);


    if (loading) return <Loader />

    return (
        <div className="page">
            <div className="page-heading">
                <div className="content pt-0 d-flex w-100 justify-content-between align-items-center">
                    <h4 className="page-title">Wallet</h4>
                </div>
            </div>
            <div className="content">
                <div className="row g-4" >
                    <div className="col-md-5 d-flex flex-column gap-4">
                        <div className="wallet-card m-0">
                            <div className="wallet-card-body">
                                <div className="wallet-stats">

                                    <div className="wallet-stat-item">
                                        <h4 className="wallet-stat-item-heading">Available Balance</h4>
                                        <h1 className={`wallet-stat-item-value ${(wallet?.statistics?.balance ?? 0) >= 0 ? 'text-success' : 'text-danger'}`}>₹{wallet?.statistics?.balance ?? 0}</h1>
                                    </div>

                                    <div className="wallet-stat-item">
                                        <h4 className="wallet-stat-item-heading">Blocked Amount <span className="mt-1"><InfoPopover heading='Why Blocked?' content='This amount is temporarily blocked until the creator successfully delivers the campaign. Once completed, it will be released to the creators.' /> </span></h4>
                                        <h1 className={`wallet-stat-item-value ${(wallet?.statistics?.blocked ?? 0) > 0 ? 'text-orange' : ''}`}>₹{wallet?.statistics?.blocked ?? 0}</h1>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-7">
                        <div className="wallet-card h-100 m-0">
                            <div className="wallet-card-body d-flex flex-column justify-content-between h-100">
                                {
                                    (wallet?.gstin?.number) ? (
                                        <>
                                            <div>
                                                <h6 className='wallet-stat-item-heading mb-3'>How to Add Funds</h6>
                                                <ol className='add-funds-instructions'>
                                                    <li>Send the desired amount using the Virtual Account details and  IFSC bank details below.</li>
                                                    <li>18% GST will be deducted from the amount added.</li>
                                                    <li>Once the transfer is processed, your wallet balance will update automatically and an invoice will be sent to you.</li>
                                                </ol>
                                            </div>
                                            {
                                                wallet?.vba ? (
                                                    <div className="vba-details">
                                                        <div className="row">
                                                            <div className="col-md-4">
                                                                <div className="vba-details-item">
                                                                    <span className="vba-details-item-label">Beneficiary Name</span>
                                                                    <span className="vba-details-item-value">{wallet?.vba?.bank_account_name}</span>
                                                                </div>
                                                            </div>
                                                            <div className="col-md-4">
                                                                <div className="vba-details-item">
                                                                    <span className="vba-details-item-label">Virtual Account No. <InfoPopover heading='Why Virtual Account?' content='We create a virtual bank account just for your brand.You transfer funds like you normally do, and we handle the rest.' size={16} /> </span>
                                                                    <span className="vba-details-item-value">{wallet?.vba?.bank_account_number} <CopyButton value={wallet?.vba?.bank_account_number} /></span>
                                                                </div>
                                                            </div>
                                                            <div className="col-md-4">
                                                                <div className="vba-details-item">
                                                                    <span className="vba-details-item-label">IFSC</span>
                                                                    <span className="vba-details-item-value">{wallet?.vba?.bank_ifsc} <CopyButton value={wallet?.vba?.bank_ifsc} /></span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <button onClick={createVBA} disabled={creatingVBA} className="btn btn-gradient">{creatingVBA ? 'Fetching...' : 'Show Bank Details'}</button>
                                                )
                                            }
                                        </>
                                    ) : (
                                        <GSTInput />
                                    )
                                }

                            </div>
                        </div>
                    </div>
                    <div className="col-md-12">
                        <div className="wallet-card p-0 m-0">
                            <h6 className="wallet-stat-item-heading px-4 pt-4">Transaction History</h6>
                            <div className="wallet-card-body">
                                <div className="d-flex align-items-center gap-3 justify-content-between mt-4 px-4 pb-4">
                                    <div className="d-flex gap-3 ps-3 align-items-center">
                                        <span className='quick-filters-text'>Quick Filters: </span>
                                        <div className="d-flex flex-wrap gap-2 align-items-center">
                                            {
                                                filters.map(filter => (<span onClick={() => {
                                                    setSelectedFilter(selectedFilter === filter.value ? '' : filter.value)
                                                }} className={`wallet-filter-badge ${selectedFilter === filter.value ? 'active' : ''}`}>{filter.label}</span>))
                                            }
                                        </div>
                                    </div>

                                    <div className="d-flex align-items-center-justify-content-end gap-3">
                                        {/* <div className="campaign-layout-search-container position-relative me-2">
                                            <span>
                                                <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M7.66732 14.0002C11.1651 14.0002 14.0007 11.1646 14.0007 7.66683C14.0007 4.16903 11.1651 1.3335 7.66732 1.3335C4.16951 1.3335 1.33398 4.16903 1.33398 7.66683C1.33398 11.1646 4.16951 14.0002 7.66732 14.0002Z" fill="#BBD8ED" stroke="#0069AD" strokeLinecap="round" strokeLinejoin="round" />
                                                    <path d="M15 16L12 13" stroke="#0069AD" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </span>
                                            <input type="text" placeholder='Search...' value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="campaign-layout-search-input" />
                                            {
                                                searchQuery && (
                                                    <span className="search-clear-icon" onClick={() => {
                                                        setSearchQuery('');
                                                    }}>
                                                        <i className='bx bx-x' />
                                                    </span>
                                                )}
                                        </div> */}
                                        <button onClick={() => dispatch(fetchWalletThunk({ force: true }))} className="btn btn-outline-primary d-flex gap-2 align-items-center">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="19" viewBox="0 0 18 19" fill="none">
                                                <path fillRule="evenodd" clipRule="evenodd" d="M15.114 0C15.5282 0 15.864 0.335786 15.864 0.75V4.99264C15.864 5.40685 15.5282 5.74264 15.114 5.74264H10.8713C10.4571 5.74264 10.1213 5.40685 10.1213 4.99264C10.1213 4.57843 10.4571 4.24264 10.8713 4.24264H13.2317C10.3863 1.99956 6.2487 2.19064 3.62348 4.81586C0.792175 7.64716 0.792175 12.2376 3.62348 15.0689C6.45478 17.9002 11.0452 17.9002 13.8765 15.0689C15.7734 13.172 16.4004 10.4852 15.7539 8.06425C15.647 7.66406 15.8848 7.25301 16.285 7.14614C16.6852 7.03927 17.0962 7.27705 17.2031 7.67724C17.9821 10.5942 17.228 13.8388 14.9372 16.1296C11.5201 19.5467 5.9799 19.5467 2.56282 16.1296C-0.854272 12.7125 -0.854272 7.17229 2.56282 3.7552C5.79483 0.523187 10.9262 0.34814 14.364 3.23006V0.75C14.364 0.335786 14.6997 0 15.114 0Z" fill="#016BAD" />
                                            </svg>
                                            Refresh
                                        </button>
                                    </div>
                                </div>
                                <div className="divider"></div>
                                <div className="table-responsive">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th className='text-start'>Description</th>
                                                <th className='text-start'>Date & Time</th>
                                                <th className='text-start'>Type</th>
                                                <th className='text-start'>CR/DR</th>
                                                <th className='text-start'>Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {
                                                txnsMatchingWithSearchQuery?.length ? (
                                                    txnsMatchingWithSearchQuery.map((txn, i) => {
                                                        if (searchQuery.trim()) {
                                                            const query = searchQuery.trim().toLowerCase();
                                                            const influencerName = txn.details?.influencer_name?.toLowerCase() || '';
                                                            const campaignName = txn.details?.campaign_name?.toLowerCase() || '';
                                                            if (!influencerName.includes(query) && !campaignName.includes(query)) {
                                                                return null;
                                                            }
                                                        }

                                                        const blocked = txn.hasOwnProperty('amount');

                                                        const credit = !blocked && 'cr_amount' in txn ? Number(txn.cr_amount ?? '0') > Number(txn.dr_amount ?? '0') : false;
                                                        return (
                                                            <tr className='border-bottom-1 px-3' key={i}>
                                                                <td className='text-start'>
                                                                    <div className="py-3">
                                                                        {
                                                                            blocked ? (txn.details?.influencer_name ?? '-') :
                                                                                txn.details?.influencer_name ? `${txn.details.influencer_name} - (${txn.details.campaign_name})` : '-'
                                                                        }
                                                                    </div>
                                                                </td>
                                                                <td className='text-start'>
                                                                    <div className="py-3">
                                                                        {txn.posted}
                                                                    </div>
                                                                </td>
                                                                <td className='text-start'>
                                                                    <div className="py-3">
                                                                        {textToTitleCase(txn.narration ?? '-')}
                                                                    </div>
                                                                </td>
                                                                <td className={`${blocked ? 'text-orange' : (credit ? 'text-success' : 'text-danger')} text-start`}>
                                                                    <div className="py-3">
                                                                        {blocked ? 'BLOCKED' : (credit ? 'CREDIT' : 'DEBIT')}
                                                                    </div>
                                                                </td>
                                                                <td className='text-start'>
                                                                    <div className="py-3">
                                                                        ₹{blocked ? (txn as any).amount : (credit ? (txn as any).cr_amount : (txn as any).dr_amount)}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )
                                                    })

                                                ) : (
                                                    <tr>
                                                        <td colSpan={5} className='text-center text-muted py-4'>
                                                            <div className="d-flex w-100 justify-content-center align-items-center my-5">
                                                                <div className="length-zero my-5">
                                                                    <h3>No transactions found!</h3>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )
                                            }
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    )
}

export default withAccessControl(Wallet, PRIVILEGE.WALLET.SHOW)
